import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FiscalYearProvider } from './context/FiscalYearContext';
import { AcademicYearProvider } from './context/AcademicYearContext';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';
import Layout from './components/Layout';
import GlobalLoadingBar from './components/GlobalLoadingBar';

// Global axios interceptor for connection errors with auto-retry
axios.interceptors.response.use(
  response => response,
  error => {
    const config = error.config;
    // Skip retry for login requests or already-retried requests
    if (!config || config._retryCount >= 2 || config.url?.includes('/auth/login')) {
      if (!error.response && error.message === 'Network Error') {
        toast.error('Connection Error', {
          description: 'Unable to connect to the server. Please check your internet connection.',
          duration: 5000,
          id: 'connection-error-final'
        });
      }
      return Promise.reject(error);
    }

    if (!error.response && error.message === 'Network Error') {
      config._retryCount = (config._retryCount || 0) + 1;
      return new Promise((resolve, reject) => {
        toast.error('Connection Error', {
          description: 'Unable to connect to the server.',
          duration: 8000,
          id: 'connection-error',
          action: {
            label: 'Retry',
            onClick: () => {
              toast.loading('Retrying...', { id: 'connection-retry', duration: 3000 });
              axios(config).then(resolve).catch(reject);
            }
          }
        });
        // Auto-reject after 10s if user doesn't click Retry
        setTimeout(() => reject(error), 10000);
      });
    }

    if (error.code === 'ECONNABORTED') {
      config._retryCount = (config._retryCount || 0) + 1;
      return new Promise((resolve, reject) => {
        toast.error('Connection Timeout', {
          description: 'The server took too long to respond.',
          duration: 8000,
          id: 'timeout-error',
          action: {
            label: 'Retry',
            onClick: () => {
              toast.loading('Retrying...', { id: 'connection-retry', duration: 3000 });
              axios(config).then(resolve).catch(reject);
            }
          }
        });
        setTimeout(() => reject(error), 10000);
      });
    }

    return Promise.reject(error);
  }
);
import PWAInstallPrompt from './components/PWAInstallPrompt';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ChartOfAccountsPage from './pages/ChartOfAccountsPage';
import VoucherEntryPage from './pages/VoucherEntryPage';
import ExchangeRatesPage from './pages/ExchangeRatesPage';
import TrialBalancePage from './pages/TrialBalancePage';
import IncomeStatementPage from './pages/IncomeStatementPage';
import GeneralLedgerPage from './pages/GeneralLedgerPage';
import SettingsPage from './pages/SettingsPage';
import CrDbNotesPage from './pages/CrDbNotesPage';
import CustomersPage from './pages/CustomersPage';
import SuppliersPage from './pages/SuppliersPage';
import InventoryPage from './pages/InventoryPage';
import SalesInvoicePage from './pages/SalesInvoicePage';
import SalesQuotationsPage from './pages/SalesQuotationsPage';
import PurchaseInvoicePage from './pages/PurchaseInvoicePage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import SalesReturnPage from './pages/SalesReturnPage';
import PurchaseReturnPage from './pages/PurchaseReturnPage';
import ImportDataPage from './pages/ImportDataPage';
import POSPage from './pages/POSPage';
import CashierPOSPage from './pages/CashierPOSPage';
import CashierSessionsPage from './pages/CashierSessionsPage';
import CashierLoginPage from './pages/CashierLoginPage';
import POSClosingReportPage from './pages/POSClosingReportPage';
import POSAnalyticsPage from './pages/POSAnalyticsPage';
import POSTransactionsPage from './pages/POSTransactionsPage';
import JournalPage from './pages/JournalPage';
// School Management pages
import SchoolDashboardPage from './pages/school/SchoolDashboardPage';
import StudentsPage from './pages/school/StudentsPage';
import FamilyGuardiansPage from './pages/school/FamilyGuardiansPage';
import TeachersPage from './pages/school/TeachersPage';
import ClassesSectionsPage from './pages/school/ClassesSectionsPage';
import FeesGroupsPage from './pages/school/FeesGroupsPage';
import FeesTypesPage from './pages/school/FeesTypesPage';
import FeesInvoiceCreatePage from './pages/school/FeesInvoiceCreatePage';
import FeesInvoicesPage from './pages/school/FeesInvoicesPage';
import FeesDashboardPage from './pages/school/FeesDashboardPage';
import FinanceStatementsPage from './pages/school/FinanceStatementsPage';
import FinanceOverduePage from './pages/school/FinanceOverduePage';
import FinanceSettingsPage from './pages/school/FinanceSettingsPage';
import AccountingJournalPage from './pages/school/AccountingJournalPage';
import SubjectsPage from './pages/school/SubjectsPage';
import AcademicYearsPage from './pages/school/AcademicYearsPage';
import EnrollmentPage from './pages/school/EnrollmentPage';
import ExamsPage from './pages/school/ExamsPage';
import GradingScalesPage from './pages/school/GradingScalesPage';
import MarksEntryPage from './pages/school/MarksEntryPage';
import OustazPage from './pages/school/OustazPage';
import AttendancePage from './pages/school/AttendancePage';
import ReportCardsPage from './pages/school/ReportCardsPage';
import TimetablePage from './pages/school/TimetablePage';
// Role portals
import StudentPortalPage from './pages/portal/StudentPortalPage';
import ParentPortalPage from './pages/portal/ParentPortalPage';
import TeacherPortalPage from './pages/portal/TeacherPortalPage';
import TeacherMyTimetablePage from './pages/portal/TeacherMyTimetablePage';
import StudentMyTimetablePage from './pages/portal/StudentMyTimetablePage';
import { LanguageProvider } from './i18n/LanguageContext';
import './App.css';

const defaultRouteForRole = (role) => {
  if (['super_admin', 'admin'].includes(role)) return '/school';
  if (role === 'teacher') return '/portal/teacher';
  if (role === 'student') return '/portal/student';
  if (role === 'parent') return '/portal/parent';
  if (role === 'cashier') return '/pos';
  return '/dashboard';
};

const ProtectedRoute = ({ children, allowedRoles, noLayout = false }) => {
  const { token, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // If allowedRoles is specified and user's role is not in the list, redirect to their home
  if (allowedRoles && user?.role && !allowedRoles.includes(user.role)) {
    return <Navigate to={defaultRouteForRole(user.role)} replace />;
  }

  // For full-screen/kiosk pages, skip the Layout wrapper
  if (noLayout) {
    return <>{children}</>;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { token, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (token) {
    return <Navigate to={defaultRouteForRole(user?.role)} replace />;
  }

  return children;
};

// Redirects authenticated users to their role's default landing page
const RoleHome = () => {
  const { user, token, loading } = useAuth();
  if (loading) return null;
  if (!token) return <Navigate to="/login" replace />;
  return <Navigate to={defaultRouteForRole(user?.role)} replace />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chart-of-accounts"
        element={
          <ProtectedRoute>
            <ChartOfAccountsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vouchers"
        element={
          <ProtectedRoute>
            <VoucherEntryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/exchange-rates"
        element={
          <ProtectedRoute>
            <ExchangeRatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trial-balance"
        element={
          <ProtectedRoute>
            <TrialBalancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/income-statement"
        element={
          <ProtectedRoute>
            <IncomeStatementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/general-ledger"
        element={
          <ProtectedRoute>
            <GeneralLedgerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cr-db-notes"
        element={
          <ProtectedRoute>
            <CrDbNotesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <CustomersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers"
        element={
          <ProtectedRoute>
            <SuppliersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <InventoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-invoices"
        element={
          <ProtectedRoute>
            <SalesInvoicePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-quotations"
        element={
          <ProtectedRoute>
            <SalesQuotationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-invoices"
        element={
          <ProtectedRoute>
            <PurchaseInvoicePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-orders"
        element={
          <ProtectedRoute>
            <PurchaseOrdersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-returns"
        element={
          <ProtectedRoute>
            <SalesReturnPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-returns"
        element={
          <ProtectedRoute>
            <PurchaseReturnPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/import-data"
        element={
          <ProtectedRoute>
            <ImportDataPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pos"
        element={
          <ProtectedRoute>
            <POSPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pos-transactions"
        element={
          <ProtectedRoute>
            <POSTransactionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cashier-sessions"
        element={
          <ProtectedRoute>
            <CashierSessionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pos-closing-report"
        element={
          <ProtectedRoute>
            <POSClosingReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pos-analytics"
        element={
          <ProtectedRoute>
            <POSAnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/cashier-login" element={<CashierLoginPage />} />
      <Route path="/cashier-pos" element={<CashierPOSPage />} />
      <Route
        path="/journal"
        element={
          <ProtectedRoute>
            <JournalPage />
          </ProtectedRoute>
        }
      />
      {/* ============ SCHOOL MANAGEMENT ============ */}
      <Route path="/school" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><SchoolDashboardPage /></ProtectedRoute>} />
      <Route path="/school/families" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><FamilyGuardiansPage /></ProtectedRoute>} />
      <Route path="/school/students" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><StudentsPage /></ProtectedRoute>} />
      <Route path="/school/teachers" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><TeachersPage /></ProtectedRoute>} />
      <Route path="/school/classes" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><ClassesSectionsPage /></ProtectedRoute>} />
      <Route path="/school/fees/groups" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}><FeesGroupsPage /></ProtectedRoute>} />
      <Route path="/school/fees/types" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}><FeesTypesPage /></ProtectedRoute>} />
      <Route path="/school/fees/invoices/new" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}><FeesInvoiceCreatePage /></ProtectedRoute>} />
      <Route path="/school/fees/invoices" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}><FeesInvoicesPage /></ProtectedRoute>} />
      <Route path="/school/fees/dashboard" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}><FeesDashboardPage /></ProtectedRoute>} />
      <Route path="/school/fees/statements" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}><FinanceStatementsPage /></ProtectedRoute>} />
      <Route path="/school/fees/overdue" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}><FinanceOverduePage /></ProtectedRoute>} />
      <Route path="/school/fees/journal" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}><AccountingJournalPage /></ProtectedRoute>} />
      <Route path="/school/fees/settings" element={<ProtectedRoute allowedRoles={['super_admin']}><FinanceSettingsPage /></ProtectedRoute>} />
      <Route path="/school/subjects" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><SubjectsPage /></ProtectedRoute>} />
      <Route path="/school/enrollment" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><EnrollmentPage /></ProtectedRoute>} />
      <Route path="/school/academic-years" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><AcademicYearsPage /></ProtectedRoute>} />
      <Route path="/school/exams" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><ExamsPage /></ProtectedRoute>} />
      <Route path="/school/grading-scales" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><GradingScalesPage /></ProtectedRoute>} />
      <Route path="/school/marks" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'teacher']}><MarksEntryPage /></ProtectedRoute>} />
      <Route path="/school/oustaz" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'teacher']}><OustazPage /></ProtectedRoute>} />
      <Route path="/school/attendance" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'teacher']}><AttendancePage /></ProtectedRoute>} />
      <Route path="/school/report-cards" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'teacher']}><ReportCardsPage /></ProtectedRoute>} />
      <Route path="/school/timetable" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'teacher']}><TimetablePage /></ProtectedRoute>} />

      {/* ============ ROLE PORTALS ============ */}
      <Route path="/portal/teacher" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin', 'admin']}><TeacherPortalPage /></ProtectedRoute>} />
      <Route path="/portal/teacher/timetable" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin', 'admin']}><TeacherMyTimetablePage /></ProtectedRoute>} />
      <Route path="/portal/student" element={<ProtectedRoute allowedRoles={['student', 'super_admin', 'admin']}><StudentPortalPage /></ProtectedRoute>} />
      <Route path="/portal/student/timetable" element={<ProtectedRoute allowedRoles={['student', 'super_admin', 'admin']}><StudentMyTimetablePage /></ProtectedRoute>} />
      <Route path="/portal/parent" element={<ProtectedRoute allowedRoles={['parent', 'super_admin', 'admin']}><ParentPortalPage /></ProtectedRoute>} />

      <Route path="/" element={<RoleHome />} />
      <Route path="*" element={<RoleHome />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
            <LanguageProvider>
              <FiscalYearProvider>
                <AcademicYearProvider>
                  <GlobalLoadingBar />
                  <AppRoutes />
                  <Toaster />
                  <PWAInstallPrompt />
                </AcademicYearProvider>
              </FiscalYearProvider>
            </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
